// LoanViewer.jsx (PROD) — with multi-select archive added
import React, { useState } from 'react';
import Swal from 'sweetalert2';
import useLoanData from '../hooks/useLoanData';
import LoanLoadStatus from './LoanLoadStatus';
import { FiUpload, FiDownload } from 'react-icons/fi';
import { handleExcelUpload } from '../services/excelUpload';
import LoanTable from './LoanTable';
import loaderGif from '../assets/loader.gif';

function LoanViewer({ currentUser }) {
  const loanData = useLoanData(currentUser, 'active');
  const { loans, setLoans, refresh: fetchLoans } = loanData;
  const [newRows, setNewRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const [searchApprovalFrom, setSearchApprovalFrom] = useState('');
  const [searchApprovalTo, setSearchApprovalTo] = useState('');
  const [searchRegion, setSearchRegion] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchLoanType, setSearchLoanType] = useState('');

  // NEW: selected loans for bulk archive
  const [selectedLoans, setSelectedLoans] = useState([]);

  const logAction = async (employee_id, action) => {
    try {
      await fetch('https://api.stitchtechsolutions.com/api/log_loan_action.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id,
          action,
          updated_by: `${currentUser.first_name} ${currentUser.last_name}`
        })
      });
    } catch (err) {
      console.warn('Logging failed', err);
    }
  };

  const handleArchiveLoan = async (loanId) => {
    const confirm = await Swal.fire({
      title: 'Archive this loan?',
      text: 'It will move to Archived Loans and no longer appear here.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, archive it',
      cancelButtonText: 'Cancel'
    });
    if (confirm.isConfirmed) {
      try {
        const response = await fetch('https://api.stitchtechsolutions.com/api/archive_loan.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ loan_id: loanId })
        });
        const result = await response.json();
        Swal.fire(result.status === 'success' ? 'Archived!' : 'Error', result.message, result.status);
        if (result.status === 'success') {
          setLoans(prev => prev.filter(loan => loan.id !== loanId));
          // also remove from selection if it was selected
          setSelectedLoans(prev => prev.filter(id => id !== loanId));
        }
      } catch (err) {
        Swal.fire('Error', 'Failed to archive the loan', 'error');
      }
    }
  };

  // NEW: toggle single loan selection for bulk archive
  const toggleSelectLoan = (loanId) => {
    setSelectedLoans((prev) =>
      prev.includes(loanId) ? prev.filter((id) => id !== loanId) : [...prev, loanId]
    );
  };

  // NEW: bulk archive handler
  const handleArchiveSelectedLoans = async () => {
    if (selectedLoans.length === 0) return;

    const loansToArchive = loans.filter((loan) => selectedLoans.includes(loan.id));

    const htmlList = loansToArchive
      .map(
        (loan) =>
          `<li><strong>${loan.client_name || 'N/A'}</strong> - ${
            loan.type_of_loan || ''
          } (ID: ${loan.id})</li>`
      )
      .join('');

    const confirm = await Swal.fire({
      title: `Archive ${selectedLoans.length} loan(s)?`,
      html: `
        <p>They will move to Archived Loans and no longer appear here.</p>
        <div style="max-height: 250px; overflow-y: auto; text-align: left;">
          <ul style="padding-left: 1.2rem; margin: 0;">
            ${htmlList}
          </ul>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, archive all',
      cancelButtonText: 'Cancel',
      width: '600px'
    });

    if (!confirm.isConfirmed) return;

    let successCount = 0;
    let failCount = 0;

    for (const loanId of selectedLoans) {
      try {
        const response = await fetch('https://api.stitchtechsolutions.com/api/archive_loan.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ loan_id: loanId })
        });
        const result = await response.json();
        if (result.status === 'success') {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }
    }

    await Swal.fire(
      'Done',
      `Successfully archived ${successCount} loan(s).${
        failCount ? `<br/>Failed: ${failCount}` : ''
      }`,
      failCount ? 'warning' : 'success'
    );

    // remove archived from current view
    setLoans((prev) => prev.filter((loan) => !selectedLoans.includes(loan.id)));
    setSelectedLoans([]);
  };

  const handleAddRow = () => {
    setNewRows(prev => [
      ...prev,
      {
        type_of_loan: '',
        approval_date: '',
        approval_time: '',
        region: '',
        employee_id: '',
        client_name: '',
        initial: '',
        verifier: '',
        loan_status: '',
        remarks: '',
        // 👇 CE Codes
        ce_code: '',          // CE Code (LMS)
        ce_code_noah: '',     // CE Code (NOAH)
        // existing fields unchanged
        noah_entry: '',
        record_position: '',
        time_and_date: new Date().toLocaleString(),
        show_to_standard: 'yes'
      }
    ]);
  };

  const handleNewRowChange = (idx, field, value) => {
    const updated = [...newRows];
    updated[idx][field] = value;
    setNewRows(updated);
  };

  const handleNewRowSave = async (row, idx) => {
    const response = await fetch('https://api.stitchtechsolutions.com/api/add_or_update_loan.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...row,
        updated_by: `${currentUser.first_name} ${currentUser.last_name}`
      })
    });

    const result = await response.json();
    Swal.fire(result.status === 'success' ? 'Success' : 'Error', result.message, result.status);
    if (result.status === 'success') {
      setLoans(prev => [row, ...prev]);
      setNewRows(prev => prev.filter((_, i) => i !== idx));
      await logAction(row.employee_id, 'Created loan entry');
    }
  };

  const handleNewRowCancel = (idx) => {
    setNewRows(prev => prev.filter((_, i) => i !== idx));
  };

  const startEditing = (loanId) => {
    const loan = loans.find(l => l.id === loanId);
    if (loan) {
      setEditingId(loanId);
      setEditingData({ ...loan });
    }
  };

  const handleEditChange = (field, value) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    const response = await fetch('https://api.stitchtechsolutions.com/api/add_or_update_loan.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...editingData,
        updated_by: `${currentUser.first_name} ${currentUser.last_name}`
      })
    });
    const result = await response.json();
    Swal.fire(result.status === 'success' ? 'Success' : 'Error', result.message, result.status);
    if (result.status === 'success') {
      const now = new Date();
      now.setHours(now.getHours() + 8);
      const gmt8DateTime = now.toISOString().slice(0, 19).replace('T', ' ');
      setLoans(prev =>
        prev.map(loan =>
          loan.id === editingId ? { ...editingData, time_and_date: gmt8DateTime } : loan
        )
      );
      setEditingId(null);
      await logAction(editingData.employee_id, 'Updated loan entry');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleToggleVisibility = async (loanId, currentVisibility) => {
    const newVisibility = currentVisibility === 'yes' ? 'no' : 'yes';
    try {
      setLoading(true);
      const response = await fetch('https://api.stitchtechsolutions.com/api/toggle_loan_visibility.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loan_id: loanId, visibility: newVisibility })
      });
      const result = await response.json();
      if (result.status === 'success') {
        setLoans(prev => prev.map(loan => loan.id === loanId ? { ...loan, show_to_standard: newVisibility } : loan));
        Swal.fire('Success', 'Visibility updated successfully', 'success');
        await logAction(loanId, `Changed visibility to ${newVisibility}`);
      } else {
        Swal.fire('Error', result.message, 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to update visibility', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      await handleExcelUpload(file, fetchLoans);
    } finally {
      setLoading(false);
    }
  };

  const filteredLoans = loans.filter(loan => {
    const matchesSearchTerm = Object.values(loan).some(val =>
      val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesApprovalFrom = searchApprovalFrom ? (loan.approval_date >= searchApprovalFrom) : true;
    const matchesApprovalTo = searchApprovalTo ? (loan.approval_date <= searchApprovalTo) : true;
    const matchesRegion = searchRegion ? (loan.region === searchRegion) : true;
    const matchesStatus = searchStatus ? (loan.loan_status === searchStatus) : true;
    const matchesLoanType = searchLoanType ? (loan.type_of_loan === searchLoanType) : true;

    return matchesSearchTerm &&
           matchesApprovalFrom &&
           matchesApprovalTo &&
           matchesRegion &&
           matchesStatus &&
           matchesLoanType;
  });

  return (
    <div>
      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <img src={loaderGif} alt="Uploading..." style={{ width: 60, height: 60 }} />
        </div>
      )}

      <h2>Loan Viewer</h2>
      <LoanLoadStatus {...loanData} />
      {currentUser?.role === 'admin' && (
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem' }}>
          <button onClick={handleAddRow}>+ Add Row</button>
          <a href="/loan_template.xlsx" download style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FiDownload /> Download Template
          </a>
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <FiUpload /> Upload Excel
            <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} style={{ display: 'none' }} />
          </label>
        </div>
      )}

      {/* Filters */}
      <div style={{
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Search:</label>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ padding: '0.5rem', width: '200px' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Approval Date From:</label>
          <input type="date" value={searchApprovalFrom} onChange={e => setSearchApprovalFrom(e.target.value)} style={{ padding: '0.4rem' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Approval Date To:</label>
          <input type="date" value={searchApprovalTo} onChange={e => setSearchApprovalTo(e.target.value)} style={{ padding: '0.4rem' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Region:</label>
          <select value={searchRegion} onChange={(e) => setSearchRegion(e.target.value)} style={{ padding: '0.4rem' }}>
            <option value="">All Regions</option>
            <option>NCR</option><option>CAR</option><option>Region I</option><option>Region II</option><option>Region III</option>
            <option>Region IV-A</option><option>Region IV-B</option><option>Region V</option><option>Region VI</option><option>Region VII</option>
            <option>Region VIII</option><option>Region IX</option><option>Region X</option><option>Region XI</option><option>Region XII</option>
            <option>Region XIII</option><option>BARMM</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Status:</label>
          <select value={searchStatus} onChange={(e) => setSearchStatus(e.target.value)} style={{ padding: '0.4rem' }}>
            <option value="">All Statuses</option>
            <option>Pending</option>
            <option>Processing</option>
            <option>Reprocessing</option>
            <option>For Final Checking</option>
            <option>Not Yet Started</option>
            <option>Hold</option>
            <option>Exceeds 30 Days Validity</option>
            <option>Disapproved</option>
            <option>Disbursed</option>
            <option>Cancelled</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Type of Loan:</label>
          <select value={searchLoanType} onChange={e => setSearchLoanType(e.target.value)} style={{ padding: '0.5rem', width: '200px' }}>
            <option value="">All Types</option>
            <option>Regular Loan</option>
            <option>PL</option>
            <option>SLI</option>
            <option>PL Convertion</option>
          </select>
        </div>
      </div>

      <LoanTable
        currentUser={currentUser}
        loans={filteredLoans}
        filterKey={JSON.stringify([searchTerm, searchApprovalFrom, searchApprovalTo, searchRegion, searchStatus, searchLoanType])}
        newRows={newRows}
        editingId={editingId}
        editingData={editingData}
        onEditChange={handleEditChange}
        onStartEdit={startEditing}
        onSaveEdit={saveEdit}
        onCancelEdit={cancelEdit}
        onNewRowChange={handleNewRowChange}
        onNewRowSave={handleNewRowSave}
        onNewRowCancel={handleNewRowCancel}
        onToggleVisibility={handleToggleVisibility}
        onArchiveLoan={handleArchiveLoan}
        // NEW props for multi-select archive:
        selectedLoans={selectedLoans}
        onToggleSelectLoan={toggleSelectLoan}
        onArchiveSelectedLoans={handleArchiveSelectedLoans}
      />
    </div>
  );
}

export default LoanViewer;
